import { useEffect, useState } from 'react';
import parse from 'html-react-parser';
import {
  RangeRoverBookFlowFormItemPropertiesModel,
  RangeRoverFlowWhereFromPropertiesModel,
  RangeRoverFlowWhereToPropertiesModel,
  RangeRoverFlowWhenPropertiesModel,
  RangeRoverFlowWhereFromElementModelContentType,
  RangeRoverFlowWhereToElementModelContentType,
  RideDto,
  RideTariffDto,
} from '@/services/platform/api/model';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { ProgressDots } from '@/components/shared/progressDots';
import { FormRow } from '@/components/shared/formRow';
import { LogoLoading } from '@/components/shared/loadings/logo';
import { WhereFromSheet } from './sheets/whereFrom';
import { WhereToSheet } from './sheets/whereTo';
import { WhenSheet } from './sheets/when';
import { cn, formatAmount, formatDate, formatTime } from '@/lib/core/utils';
import {
  cancelInProgressRides,
  createRide,
  getRideTariffs,
  RideAlreadyInProgressError,
  RideServiceUnavailableError,
} from '@/services/platform/data/booking/rides';
import { BookingCancelSheet } from '@/components/shared/booking/actions/cancel-sheet';
import type { BookingState, OpenSheet, RangeRoverBookProps } from './types';
import { getPickupTimeIso } from './utils';
import { useClientTranslation } from '@/lib/core/i18n';
import { useLocaleStore } from '@/lib/core/stores';
import { ScreenHead } from '@/components/shared/screen/head';

// ── RangeRoverBook ────────────────────────────────────────────────────────────

export function RangeRoverBook({
  cmsProps,
  booking,
  onBookingChange,
  onBack,
  onBookNow,
  onRideCreated,
  serviceCategory,
  step,
}: RangeRoverBookProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);
  const [openSheet, setOpenSheet] = useState<OpenSheet>(null);
  const [tariff, setTariff] = useState<RideTariffDto | null>(null);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingProgress, setBookingProgress] = useState(0);
  const [rideInProgressOpen, setRideInProgressOpen] = useState(false);
  const [serviceUnavailableOpen, setServiceUnavailableOpen] = useState(false);

  const formItems = (cmsProps?.bookForm?.items ?? []).map(
    item => item.content?.properties as RangeRoverBookFlowFormItemPropertiesModel | undefined
  );

  // The "When" form item is only shown once the "Schedule ride" toggle is on —
  // it's rendered separately from the regular location rows below.
  const locationItems = formItems.filter(formItem => !formItem?.isWhenFormItem);
  const whenItem = formItems.find(formItem => formItem?.isWhenFormItem);

  const titleHtml = cmsProps?.title?.markup;
  const continueLabel = cmsProps?.bookARideLabel ?? t('App.Labels.Continue');

  const locationsSet = booking.pickupPoint !== null && booking.dropoffPoint !== null;
  const canContinue = locationsSet && (!booking.isScheduled || (!!booking.date && !!booking.time));

  const pickupDisplay = booking.pickupPoint?.name ?? '';
  const dropoffDisplay = booking.dropoffPoint?.name ?? '';
  const whenDisplay =
    booking.date && booking.time
      ? `${formatDate(booking.date.toISOString())}, ${formatTime(booking.time)}`
      : '';

  const rowIsSet = locationItems.map(formItem => {
    const stepType = formItem?.stepPicker?.items?.[0]?.content?.contentType;
    switch (stepType) {
      case RangeRoverFlowWhereFromElementModelContentType.rangeRoverFlowWhereFrom:
        return !!booking.pickupPoint;
      case RangeRoverFlowWhereToElementModelContentType.rangeRoverFlowWhereTo:
        return !!booking.dropoffPoint;
      default:
        return false;
    }
  });

  const isRowDisabled = (index: number) => index > 0 && !rowIsSet.slice(0, index).every(Boolean);

  useEffect(() => {
    const { pickupPoint, dropoffPoint } = booking;
    if (
      !serviceCategory?.properties?.hasPayment ||
      pickupPoint?.lat == null ||
      pickupPoint?.lng == null ||
      dropoffPoint?.lat == null ||
      dropoffPoint?.lng == null
    ) {
      setTariff(null);
      return;
    }

    let cancelled = false;
    getRideTariffs(
      {
        originLat: pickupPoint.lat,
        originLng: pickupPoint.lng,
        destinationLat: dropoffPoint.lat,
        destinationLng: dropoffPoint.lng,
        //vehicleType: 'TESLA_MODEL_3',
        //numberOfSeats: 2,
        pickupTime: getPickupTimeIso(booking),
      },
      locale
    ).then(tariffs => {
      if (!cancelled && tariffs) setTariff(tariffs?.[0] ?? null);
    });

    return () => {
      cancelled = true;
    };
  }, [booking.pickupPoint, booking.dropoffPoint, booking.date, booking.time, locale, serviceCategory]);

  // Books immediately and routes to whichever next step applies: scheduled rides
  // jump straight to confirmation (skipping driver-info entirely, since a future
  // pickup has no "finding a driver now" phase); ASAP rides jump to the driver-info
  // step, which shows the finding-driver/live-tracking screen.
  const createRideAndProceed = async (onSuccess: (ride: RideDto) => void) => {
    const { pickupPoint, dropoffPoint } = booking;
    if (
      pickupPoint?.lat == null ||
      pickupPoint?.lng == null ||
      dropoffPoint?.lat == null ||
      dropoffPoint?.lng == null
    ) {
      return;
    }

    const pickup = { lat: pickupPoint.lat, lng: pickupPoint.lng };
    const dropoff = { lat: dropoffPoint.lat, lng: dropoffPoint.lng };
    const pickupTime = getPickupTimeIso(booking);

    setIsBooking(true);
    setBookingProgress(0);

    let current = 0;
    const interval = setInterval(() => {
      current += (0.85 - current) * 0.1;
      setBookingProgress(current);
    }, 80);

    let ride;
    try {
      const tariffs = serviceCategory?.properties?.hasPayment
        ? await getRideTariffs(
            {
              originLat: pickup.lat,
              originLng: pickup.lng,
              destinationLat: dropoff.lat,
              destinationLng: dropoff.lng,
              pickupTime,
            },
            locale
          )
        : undefined;
      const bookedTariff = tariffs?.[0];

      ride = await createRide(
        {
          pickup,
          pickupAddress: pickupPoint.address ?? pickupPoint.name,
          pickupName: pickupPoint.name,
          pickupCity: pickupPoint.city,
          dropoff,
          dropoffAddress: dropoffPoint.address ?? dropoffPoint.name,
          dropoffName: dropoffPoint.name,
          dropoffCity: dropoffPoint.city,
          vehicleType: bookedTariff?.vehicleType ?? undefined,
          tariffId: bookedTariff?.tariffId ?? undefined,
          pickupTime,
          serviceCategoryNodeKey: serviceCategory?.id ?? '',
        },
        locale
      );
    } catch (error) {
      if (error instanceof RideAlreadyInProgressError) {
        setIsBooking(false);
        setRideInProgressOpen(true);
        return;
      }
      if (error instanceof RideServiceUnavailableError) {
        setIsBooking(false);
        setServiceUnavailableOpen(true);
        return;
      }
      throw error;
    } finally {
      clearInterval(interval);
    }

    if (!ride) {
      setIsBooking(false);
      throw new Error('Booking failed');
    }

    setBookingProgress(1);
    await new Promise(r => setTimeout(r, 400));
    onSuccess(ride);
  };

  const isAsap = !(booking.isScheduled && booking.date && booking.time);

  const handlePrimaryAction = () => {
    createRideAndProceed(isAsap ? onRideCreated : onBookNow);
  };

  const handlePickupChange = (updates: Partial<BookingState>) =>
    onBookingChange(
      'pickupPoint' in updates
        ? {
            ...updates,
            dropoffPoint: null,
            isScheduled: false,
            date: null,
            time: null,
          }
        : updates
    );

  const handleDropoffChange = (updates: Partial<BookingState>) =>
    onBookingChange(
      'dropoffPoint' in updates
        ? {
            ...updates,
            isScheduled: false,
            date: null,
            time: null,
          }
        : updates
    );

  const handleScheduleToggle = (checked: boolean) =>
    onBookingChange(checked ? { isScheduled: true } : { isScheduled: false, date: null, time: null });

  // Onde was unreachable when we tried to book — the pickup/dropoff/time the user
  // picked may be stale by the time the service is back, so clear the form instead
  // of silently retrying with the same (possibly now-invalid) parameters.
  const handleResetBooking = () =>
    onBookingChange({
      pickupPoint: null,
      dropoffPoint: null,
      isScheduled: false,
      date: null,
      time: null,
    });

  if (isBooking) {
    return (
      <LogoLoading
        progress={bookingProgress}
        className="bg-white"
        caption={
          isAsap
            ? { title: t('App.Labels.FindingDriverTitle'), subtitle: t('App.Labels.FindingDriverSubtitle') }
            : undefined
        }
      />
    );
  }

  return (
    <>
      {/* Header */}
      <div className="pt-safe-area relative shrink-0 bg-white">
        <div className="relative min-h-15 w-full">
          <div className={cn('pt-safe-area fixed top-0 z-80 flex w-full bg-white')}>
            <ScreenHead
              title={''}
              onBack={onBack}
              className="pt-0"
              rightActions={<ProgressDots step={step} />}
            />
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex flex-1 flex-col gap-8 overflow-y-auto px-4 pt-6 pb-66">
        {titleHtml && <div className="book-intro-title">{parse(titleHtml)}</div>}

        {/* Form rows */}
        <div className="flex flex-col gap-6">
          {locationItems.map((formItem, index) => {
            const step = formItem?.stepPicker?.items?.[0];
            const stepTitle = formItem?.title;
            const stepType = step?.content?.contentType;
            switch (stepType) {
              case RangeRoverFlowWhereFromElementModelContentType.rangeRoverFlowWhereFrom:
                return (
                  <FormRow
                    key={index}
                    label={stepTitle ?? t('App.Labels.WhereFrom')}
                    value={pickupDisplay}
                    isEmpty={!booking.pickupPoint}
                    disabled={isRowDisabled(index)}
                    onClick={() => setOpenSheet('wherefrom')}
                  />
                );
              case RangeRoverFlowWhereToElementModelContentType.rangeRoverFlowWhereTo:
                return (
                  <FormRow
                    key={index}
                    label={stepTitle ?? t('App.Labels.WhereTo')}
                    value={dropoffDisplay}
                    isEmpty={!booking.dropoffPoint}
                    disabled={isRowDisabled(index)}
                    onClick={() => setOpenSheet('whereto')}
                  />
                );
              default:
                return null;
            }
          })}

          {/* Schedule ride toggle — the "When" row below only appears once this is on */}
          <Switch
            checked={booking.isScheduled}
            onCheckedChange={handleScheduleToggle}
            disabled={!locationsSet}
            label={t('App.Labels.ScheduleRide')}
          />

          {booking.isScheduled && whenItem && (
            <FormRow
              label={whenItem.title ?? t('App.Labels.When')}
              value={whenDisplay}
              isEmpty={!booking.date || !booking.time}
              onClick={() => setOpenSheet('when')}
            />
          )}
        </div>
      </div>
      {/* Cancellation policy */}
      {(cmsProps?.cancellationPolicyTitle || cmsProps?.cancellationPolicySubtitle) && (
        <div className="fixed bottom-[119px] flex w-[calc(100vw-32px)] w-full flex-col gap-1 bg-white px-4 py-4">
          {cmsProps?.cancellationPolicyTitle && (
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] font-medium tracking-[0.5px]">
              {cmsProps.cancellationPolicyTitle}
            </p>
          )}
          {cmsProps?.cancellationPolicySubtitle && (
            <p className="font-pp-neue-montreal text-jncquoi-neutral-600 text-[12px] leading-[100%] tracking-[0.5px]">
              {cmsProps.cancellationPolicySubtitle}
            </p>
          )}
        </div>
      )}

      {/* Sticky continue button */}
      <div className="fixed bottom-0 w-full shrink-0 border-t-[0.5px] border-[#c8c8c8] bg-white px-4 pt-6 pb-10.5">
        <div className="flex items-center gap-4">
          <Button
            variant="primary-solid"
            palette="dark"
            size="lg"
            className="flex-1"
            disabled={!canContinue}
            onClick={handlePrimaryAction}
          >
            {continueLabel}
          </Button>
          {tariff && tariff?.cost && (
            <div className="shrink-0 text-right">
              <p className="font-pp-neue-montreal text-[14px] font-semibold text-[#1a1a1a]">
                {formatAmount(tariff?.cost ?? tariff?.minimumCharge, tariff.currency, locale)}
              </p>
              <p className="font-pp-neue-montreal text-[12px] text-[#666]">{t('App.Labels.TaxesIncluded')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Sheets */}

      {locationItems.map((formItem, index) => {
        const step = formItem?.stepPicker?.items?.[0];
        const stepProps = step?.content?.properties;
        const stepType = step?.content?.contentType;
        switch (stepType) {
          case RangeRoverFlowWhereFromElementModelContentType.rangeRoverFlowWhereFrom:
            return (
              <WhereFromSheet
                key={index}
                isOpen={openSheet === 'wherefrom'}
                onClose={() => setOpenSheet(null)}
                cmsProps={stepProps as RangeRoverFlowWhereFromPropertiesModel}
                booking={booking}
                onBookingChange={handlePickupChange}
              />
            );
          case RangeRoverFlowWhereToElementModelContentType.rangeRoverFlowWhereTo:
            return (
              <WhereToSheet
                key={index}
                isOpen={openSheet === 'whereto'}
                onClose={() => setOpenSheet(null)}
                cmsProps={stepProps as RangeRoverFlowWhereToPropertiesModel}
                booking={booking}
                onBookingChange={handleDropoffChange}
              />
            );
          default:
            return null;
        }
      })}

      {booking.isScheduled && whenItem && (
        <WhenSheet
          isOpen={openSheet === 'when'}
          onClose={() => setOpenSheet(null)}
          cmsProps={whenItem.stepPicker?.items?.[0]?.content?.properties as RangeRoverFlowWhenPropertiesModel}
          booking={booking}
          onBookingChange={onBookingChange}
        />
      )}

      <BookingCancelSheet
        open={rideInProgressOpen}
        onOpenChange={setRideInProgressOpen}
        title={t('App.Labels.RideInProgressTitle')}
        description={t('App.Labels.RideInProgressText')}
        cancelLabel={t('App.Labels.CancelRide')}
        keepLabel={t('App.Labels.WaitForRide')}
        onConfirmCancel={async () => {
          const success = await cancelInProgressRides(locale);
          if (!success) throw new Error('Failed to cancel in-progress ride');
        }}
      />

      <BookingCancelSheet
        open={serviceUnavailableOpen}
        onOpenChange={setServiceUnavailableOpen}
        title={t('App.Labels.ServiceUnavailableTitle')}
        description={t('App.Labels.ServiceUnavailableText')}
        cancelLabel={t('App.Labels.ResetBooking')}
        keepLabel={t('App.Labels.Close')}
        onConfirmCancel={async () => {
          handleResetBooking();
        }}
      />
    </>
  );
}
